import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

const WhoWeAre = ({ content }) => {
  if (!content) return null;

  const paragraphs = content.who_we_are_text?.split('\n\n') || [];

  const categories = [
    {
      title: 'ACCOUNTABILITY',
      links: [
        { label: "Trump's Executive Order", url: '/trump-executive-order' },
        { label: 'Voter Roll Maintenance', url: '/score-cards' },
        { label: 'Board of Elections Compliance', url: '/election-board-compliance' }
      ]
    },
    {
      title: 'LEGISLATION',
      links: [
        { label: 'Voter ID', url: '/voter-id' },
        { label: 'Signature Verification', url: '/signature-verification' },
        { label: 'Voter Bill of Rights', url: 'https://www.electionintegritynetwork.org/elections-bill-of-rights', external: true }
      ]
    },
    {
      title: 'ADVOCACY',
      links: [
        { label: 'Election Accuracy Day of Action', url: '/day-of-action' },
        { label: 'Testimonies', url: '/testimonies' },
        { label: 'Petition', url: '/sign-the-petition' },
        { label: 'Expert Guidance', url: '/expert-guidance' },
        { label: 'Citizen Action', url: '/citizen-action' }
      ]
    }
  ];

  return (
    <>
      {/* Who We Are Section */}
      <section className="pt-20 pb-32 bg-white overflow-hidden" data-testid="who-we-are-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Images — staggered entrance */}
            <div className="flex gap-4">
              <motion.div
                className="w-1/2"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: "-80px" }}
              >
                <img
                  src="https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-30-scaled.jpg"
                  alt="Team members"
                  className="w-full h-[400px] object-cover rounded-lg shadow-lg"
                />
              </motion.div>
              <motion.div
                className="w-1/2 pt-12"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: "-80px" }}
              >
                <img
                  src="https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-76-scaled.jpg"
                  alt="Team at work"
                  className="w-full h-[400px] object-cover rounded-lg shadow-lg"
                />
              </motion.div>
            </div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              viewport={{ once: true, margin: "-80px" }}
            >
              <span className="text-sm font-semibold text-[#F6BF58] tracking-wider">
                {content.who_we_are_title}
              </span>
              <div className="mt-6 space-y-4">
                {paragraphs.map((paragraph, index) => (
                  <motion.p
                    key={index}
                    className="text-slate-600 leading-relaxed"
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                    viewport={{ once: true, margin: "-80px" }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Category Cards — Overlapping Section */}
      <div className="relative">
        <div className="absolute inset-0">
          <div className="h-1/2 bg-white"></div>
          <div className="h-1/2 bg-[#f5f5f5]"></div>
        </div>
        
        <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-8">
          {/* Gold line */}
          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            viewport={{ once: true, margin: "-60px" }}
          >
            <motion.div
              className="w-24 h-1 bg-[#F6BF58]"
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true, margin: "-60px" }}
            />
          </motion.div>
          
          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, margin: "-40px" }}
                className="group bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.1)] overflow-hidden text-center hover:bg-[#F6BF58] transition-all duration-300 cursor-pointer"
                data-testid={`category-card-${category.title.toLowerCase()}`}
              >
                <div className="h-1.5 bg-[#F6BF58] group-hover:bg-white transition-colors duration-300"></div>
                <div className="p-8">
                  <div className="w-16 h-16 rounded-full bg-[#F6BF58] group-hover:bg-white flex items-center justify-center mx-auto mb-6 transition-colors duration-300">
                    <Check className="text-[#9E1B32]" size={32} strokeWidth={3} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 group-hover:text-white uppercase tracking-wide mb-4 transition-colors duration-300">
                    {category.title}
                  </h3>
                  <p className="text-[#9E1B32] text-sm leading-relaxed">
                    {category.links.map((link, linkIndex) => (
                      <span key={linkIndex}>
                        {link.external ? (
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {link.label}
                          </a>
                        ) : (
                          <Link to={link.url} className="hover:underline">
                            {link.label}
                          </Link>
                        )}
                        {linkIndex < category.links.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default WhoWeAre;

